import { onIdTokenChanged } from 'firebase/auth';
import { WritableDraft } from 'immer';
import React, {
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useImmerReducer } from 'use-immer';

import {
  clearAuthErrorHandler,
  queryClient,
  setAuthErrorHandler,
  setAuthToken,
  useRefreshSession,
} from '~/apis';
import { type AuthFailureContext, isRequestError } from '~/apis/api';
import { useFirebaseAuth } from '~/apis/useFirebase';
import { config, mockedAuthToken, uiLogger } from '~/app';
import { clearAuthToken, updateAuthToken } from '~/app/authStorage';
import { clearLocalGameSnapshot } from '~/app/controllerLocalWrapper';
import {
  destroyControllerRemoteWrapper,
  getControllerRemoteWrapper,
} from '~/app/controllerRemoteWrapper';
import { authPersistedStateSchema, refreshLockSchema } from '~/app/storageSchemas';
import { setCurrentStorageUserScopeId } from '~/app/storageScope';
import { safeParseStoredJson } from '~/app/storageValidation';
import { hmrSafeContext } from '~/utils/hmrSafeContext';

import { isJwtExpired, JWT_HYDRATE_EXPIRY_SKEW_MS } from './jwt';

type AuthState = 'initializing' | 'idle' | 'login' | 'register';
type State = {
  token?: string;
  firebaseIdToken?: string;
  state: AuthState;
  /** True when past recovery window: either no persisted session or startup refresh attempt finished. */
  authSettled: boolean;
};

type RefreshReason = 'startup' | 'protected401' | 'firebase' | 'manual';
type CrossTabAuthMessage =
  | { type: 'refresh-start' }
  | { type: 'refresh-success'; token: string; firebaseIdToken?: string }
  | { type: 'refresh-failed' }
  | { type: 'logout' };

type Action =
  | { type: 'hydrate'; payload: Omit<State, 'authSettled'> }
  | {
      type: 'commitCredentials';
      payload: { token: string; firebaseIdToken?: string };
    }
  | { type: 'login' }
  | { type: 'register' }
  | { type: 'cancel' }
  | { type: 'logout' }
  | { type: 'logoutAndClearData' }
  | { type: 'authSettled' };
type ExternalState = State & { isAuthorized: boolean; isAuthSettled: boolean };

const REFRESH_LOCK_KEY = 'auth-refresh-lock';
const REFRESH_LOCK_TTL_MS = 15_000;

type RefreshLock = {
  ownerId: string;
  expiresAt: number;
};

type AuthFunctions = {
  clearAuthData: () => void;
  clearAuthDataAndTraining: () => void;
};

const AuthStateContext = hmrSafeContext<ExternalState | undefined>(
  '__turfmaster_auth_state_ctx__',
  undefined,
);
const AuthDispatchContext = hmrSafeContext<Dispatch<Action> | undefined>(
  '__turfmaster_auth_dispatch_ctx__',
  undefined,
);
const AuthFunctionsContext = hmrSafeContext<AuthFunctions | undefined>(
  '__turfmaster_auth_functions_ctx__',
  undefined,
);

const authReducer = (draft: WritableDraft<State>, action: Action) => {
  switch (action.type) {
    case 'commitCredentials': {
      const { token, firebaseIdToken } = action.payload;
      draft.token = token;
      if (typeof firebaseIdToken === 'string' && firebaseIdToken.length > 0) {
        draft.firebaseIdToken = firebaseIdToken;
      }
      break;
    }
    case 'login': {
      draft.state = 'login';
      break;
    }
    case 'register': {
      draft.state = 'register';
      break;
    }
    case 'cancel': {
      draft.state = 'idle';
      break;
    }
    case 'logout': {
      draft.state = 'idle';
      draft.token = undefined;
      draft.firebaseIdToken = undefined;
      break;
    }
    case 'logoutAndClearData': {
      draft.state = 'idle';
      draft.token = undefined;
      draft.firebaseIdToken = undefined;
      break;
    }
    case 'authSettled': {
      draft.authSettled = true;
      // No-credentials bootstrap dispatches only authSettled (no hydrate). Without leaving
      // 'initializing', ProtectedRouteGuard stays on the loading spinner forever for logged-out users.
      if (draft.state === 'initializing') {
        draft.state = 'idle';
      }
      break;
    }
    case 'hydrate': {
      const { firebaseIdToken, token } = action.payload;
      // Always set to 'idle' on restore. Do not restore 'login'/'register' (would auto-open
      // dialog) or 'initializing' (would leave app stuck). Invalid/undefined also become 'idle'.
      draft.state = 'idle';
      // Only restore credentials when they look like valid strings (avoid corrupted/legacy storage).
      draft.token = typeof token === 'string' && token.length > 0 ? token : undefined;
      draft.firebaseIdToken =
        typeof firebaseIdToken === 'string' && firebaseIdToken.length > 0
          ? firebaseIdToken
          : undefined;
      // Intentionally do NOT reset authSettled here. The initial state is already unsettled, so
      // startup hydration needs no reset. The cross-tab `storage` handler also dispatches `hydrate`
      // when another tab rotates the access token (e.g. a profile opened in a new tab runs its own
      // startup /auth/refresh). Resetting authSettled there would knock an already-settled tab back
      // into the loading state, and since that tab's startup refresh already ran, nothing re-settles
      // it — leaving ProtectedRouteGuard stuck on an endless spinner.
      break;
    }
  }
};

export interface AuthProviderProps {
  children: ReactNode;
}

const initializingState: State = { state: 'initializing', authSettled: false };

// Re-export authStorage for backward compatibility
export { authStorage } from '~/app/authStorage';

const persistKey = 'auth';

/** Firebase Auth errors that are transient (network/timeout/rate limit). Do not clear auth on these. */
const FIREBASE_TRANSIENT_ERROR_CODES = new Set([
  'auth/network-request-failed',
  'auth/too-many-requests',
  'auth/timeout',
  'auth/internal-error',
]);

function isFirebaseTransientError(error: unknown): boolean {
  const code =
    error && typeof error === 'object' && 'code' in error ? (error as { code: string }).code : '';
  return typeof code === 'string' && FIREBASE_TRANSIENT_ERROR_CODES.has(code);
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [state, dispatch] = useImmerReducer(authReducer, initializingState);
  const [crossTabChannel] = useState<BroadcastChannel | null>(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return null;
    }
    return new BroadcastChannel('turfmaster-auth');
  });
  const postCrossTabMessage = useCallback(
    (message: CrossTabAuthMessage): void => {
      if (!crossTabChannel) return;
      try {
        crossTabChannel.postMessage(message);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'InvalidStateError') {
          return;
        }
        uiLogger.debug(
          { err: error, messageType: message.type },
          'broadcastChannel postMessage failed',
        );
      }
    },
    [crossTabChannel],
  );

  /** Single full cleanup path for auth invalidation: persisted auth, authStorage, request auth token, query cache, remote controller. */
  const clearAuthData = useCallback(() => {
    // Debug breadcrumb: helps identify unexpected logout triggers (e.g. refresh races).
    // Intentionally logs a stack trace so we can pinpoint the caller quickly.
    uiLogger.warn({ stack: new Error('clearAuthData').stack }, 'AuthContext: clearing auth data');
    dispatch({ type: 'logout' });
    localStorage.removeItem('auth');
    queryClient.clear();
    clearAuthToken();
    setAuthToken(undefined);
    setCurrentStorageUserScopeId(undefined);
    destroyControllerRemoteWrapper();
    postCrossTabMessage({ type: 'logout' });
  }, [dispatch, postCrossTabMessage]);

  const clearAuthDataAndTraining = useCallback(() => {
    clearAuthData();
    clearLocalGameSnapshot();
  }, [clearAuthData]);

  const restoreStatus = useRef({
    storage: false,
    firebase: false,
    /** True when we restored token from localStorage; avoid clearing on Firebase's initial null before it has restored. */
    restoredCredentialsFromStorage: false,
  });
  const isRefreshingSessionRef = useRef(false);
  const inFlightRefreshPromiseRef = useRef<Promise<void> | null>(null);
  const startupRefreshAttemptedRef = useRef(false);
  const tabIdRef = useRef<string | null>(null);
  const lastSeenAccessTokenRef = useRef<string | null>(null);

  const ensureTabId = useCallback(() => {
    if (tabIdRef.current) return tabIdRef.current;
    // Prefer crypto.randomUUID when available, fall back to a simple random string.
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    tabIdRef.current = id;
    return id;
  }, []);

  const readRefreshLock = useCallback((): RefreshLock | null => {
    if (typeof window === 'undefined') return null;
    const parsed = safeParseStoredJson(localStorage.getItem(REFRESH_LOCK_KEY), refreshLockSchema);
    if (parsed.ok) return parsed.data as RefreshLock;
    if (parsed.reason !== 'missing') {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
    return null;
  }, []);

  const writeRefreshLock = useCallback((lock: RefreshLock) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
    } catch {
      // ignore storage failures, fall back to per-tab guard
    }
  }, []);

  const clearRefreshLockIfOwned = useCallback(() => {
    if (typeof window === 'undefined') return;
    const tabId = tabIdRef.current;
    if (!tabId) return;
    try {
      const current = readRefreshLock();
      if (current && current.ownerId === tabId) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
      }
    } catch {
      // ignore
    }
  }, [readRefreshLock]);

  const auth = useFirebaseAuth();
  const { mutateAsync: callRefreshSessionAsync } = useRefreshSession();

  /** Single refresh pipeline shared by all triggers.
   *  - Per-tab: runs at most once per tab at a time.
   *  - Cross-tab: uses a short-lived lock so typically only one tab talks to /auth/refresh; others wait for its result.
   *  - Clears auth only on confirmed invalid session (401 on refresh or empty response).
   */
  const refreshSessionIfPossible = useCallback(
    async (options?: { firebaseIdToken?: string; reason?: RefreshReason }) => {
      const reason: RefreshReason = options?.reason ?? 'manual';
      // Startup refresh uses the HttpOnly cookie; access JWT may be omitted when clearly expired.
      if (!state.token && reason !== 'startup') return;

      const now = Date.now();

      // Try to coordinate across tabs using a short-lived lock.
      if (typeof window !== 'undefined') {
        const tabId = ensureTabId();
        const existingLock = readRefreshLock();
        const existingValid = existingLock && existingLock.expiresAt > now;

        // If there is no valid lock, try to become leader.
        if (!existingValid) {
          const newLock: RefreshLock = {
            ownerId: tabId,
            expiresAt: now + REFRESH_LOCK_TTL_MS,
          };
          writeRefreshLock(newLock);
          const confirmed = readRefreshLock();
          // If we failed to confirm ownership, treat this as follower.
          if (!confirmed || confirmed.ownerId !== tabId) {
            // fall through to follower logic below
          }
        }

        const finalLock = readRefreshLock();
        const finalValid = finalLock && finalLock.expiresAt > Date.now();

        if (finalValid && finalLock!.ownerId !== tabId) {
          // Another still-valid lock exists and is owned by a different tab/page instance.
          //
          // Why we intentionally "do nothing" (return) instead of waiting:
          // - In reload storms, the previous page instance may set the lock and then get destroyed
          //   before it can broadcast refresh-success/refresh-failed.
          // - If we wait as a follower for that broadcast, we can hang in an "in-between" auth state
          //   (header lock icon, missing account, endless spinner) even though credentials are valid.
          // - Returning here keeps this call non-blocking; the startup effect still guarantees
          //   `authSettled` in a finally block, so UI recovery continues deterministically.
          //
          // Net effect: avoid dead-wait behavior while still preventing duplicate refresh requests.
          return;
        }
      }

      if (inFlightRefreshPromiseRef.current) {
        return inFlightRefreshPromiseRef.current;
      }

      if (isRefreshingSessionRef.current) return;
      isRefreshingSessionRef.current = true;

      const refreshPromise = (async () => {
        try {
          postCrossTabMessage({ type: 'refresh-start' });

          const tokenResponse = await callRefreshSessionAsync();
          if (tokenResponse) {
            const nextFirebaseIdToken = options?.firebaseIdToken ?? state.firebaseIdToken;
            dispatch({
              type: 'commitCredentials',
              payload: {
                token: tokenResponse.token,
                firebaseIdToken: nextFirebaseIdToken,
              },
            });
            postCrossTabMessage({
              type: 'refresh-success',
              token: tokenResponse.token,
              firebaseIdToken: nextFirebaseIdToken,
            });
          } else {
            // Treat empty refresh response as transient (do not log out).
            // The backend may be temporarily unavailable or return an empty body; clearing auth would
            // cause flaky "logged out on reload" behavior even though the session may still be valid.
            uiLogger.warn('AuthContext: refresh returned empty response; keeping current session');
            postCrossTabMessage({ type: 'refresh-failed' });
          }
        } catch (error) {
          if (isRequestError(error) && error.status === 401) {
            if (reason === 'protected401') {
              clearAuthData();
            } else {
              uiLogger.warn(
                { reason },
                'AuthContext: refresh returned 401; keeping current session',
              );
              postCrossTabMessage({ type: 'refresh-failed' });
            }
          } else {
            uiLogger.warn(
              { err: error },
              'transient error during auth refresh; keeping current session',
            );
            postCrossTabMessage({ type: 'refresh-failed' });
          }
        } finally {
          clearRefreshLockIfOwned();
          isRefreshingSessionRef.current = false;
          inFlightRefreshPromiseRef.current = null;
        }
      })();

      inFlightRefreshPromiseRef.current = refreshPromise;
      return refreshPromise;
    },
    [
      state.token,
      state.firebaseIdToken,
      dispatch,
      callRefreshSessionAsync,
      clearAuthData,
      clearRefreshLockIfOwned,
      ensureTabId,
      postCrossTabMessage,
      readRefreshLock,
      writeRefreshLock,
    ],
  );

  // Notify on 401 only. Try one refresh before clearing.
  const onProtectedRequest401 = useCallback(
    (_context: AuthFailureContext) => {
      refreshSessionIfPossible({ reason: 'protected401' });
    },
    [refreshSessionIfPossible],
  );

  // Register global auth error handler for 401 on protected requests
  useEffect(() => {
    setAuthErrorHandler(onProtectedRequest401);

    return () => {
      clearAuthErrorHandler();
    };
  }, [onProtectedRequest401]);

  // hydrate initial state from local storage if available
  useEffect(() => {
    if (state.state !== 'initializing') return;

    // initially set up state (ssr compatible)
    const restoredParsed = safeParseStoredJson(
      localStorage.getItem(persistKey),
      authPersistedStateSchema,
    );
    const restoredState = restoredParsed.ok ? (restoredParsed.data as State) : ({} as State);
    if (!restoredParsed.ok && restoredParsed.reason !== 'missing') {
      localStorage.removeItem(persistKey);
    }
    const hasRestoredCredentials =
      typeof restoredState?.token === 'string' &&
      restoredState.token.length > 0 &&
      typeof restoredState?.firebaseIdToken === 'string' &&
      restoredState.firebaseIdToken.length > 0;

    const tokenClearlyExpired =
      typeof restoredState?.token === 'string' &&
      restoredState.token.length > 0 &&
      isJwtExpired(restoredState.token, JWT_HYDRATE_EXPIRY_SKEW_MS);
    const hydratePayload: Omit<State, 'authSettled'> = tokenClearlyExpired
      ? { ...restoredState, token: undefined }
      : restoredState;

    if (hasRestoredCredentials) {
      restoreStatus.current.storage = true;
      restoreStatus.current.restoredCredentialsFromStorage = true;
      if (!tokenClearlyExpired) {
        // Sync token to authStorage immediately (before dispatch) so getAuthToken() is available
        // as soon as we hydrate; sagas and other code can rely on it without waiting for the next render.
        updateAuthToken(restoredState.token!);
      }
      dispatch({ type: 'hydrate', payload: hydratePayload });
    } else if (restoredState && (restoredState.token || restoredState.firebaseIdToken)) {
      restoreStatus.current.storage = true;
      if (
        typeof hydratePayload.token === 'string' &&
        hydratePayload.token.length > 0 &&
        typeof hydratePayload.firebaseIdToken === 'string' &&
        hydratePayload.firebaseIdToken.length > 0
      ) {
        restoreStatus.current.restoredCredentialsFromStorage = true;
      }
      if (
        typeof hydratePayload.token === 'string' &&
        hydratePayload.token.length > 0 &&
        !tokenClearlyExpired
      ) {
        updateAuthToken(hydratePayload.token);
      }
      dispatch({ type: 'hydrate', payload: hydratePayload });
    } else {
      restoreStatus.current.storage = true;

      // firebase already tried to restore so we only waited for storage and can clear auth data
      if (restoreStatus.current.firebase) {
        uiLogger.debug('cleared auth data');
        clearAuthData();
      } else {
        uiLogger.debug('skipped clearing auth data, because firebase could potentially restore it');
      }
      dispatch({ type: 'authSettled' });
    }
  }, [clearAuthData, dispatch, state.state]);

  // Set up cross-tab coordination (BroadcastChannel with graceful fallback disabled for now if unavailable)
  useEffect(() => {
    if (!crossTabChannel) return;

    const onCrossTabMessage = (event: MessageEvent) => {
      const message = event.data as CrossTabAuthMessage;

      switch (message.type) {
        case 'refresh-success': {
          dispatch({
            type: 'commitCredentials',
            payload: {
              token: message.token,
              firebaseIdToken: message.firebaseIdToken,
            },
          });
          break;
        }
        case 'logout': {
          clearAuthData();
          break;
        }
        case 'refresh-start':
        case 'refresh-failed':
          break;
        default:
          // Other tabs don't need to take action on these events yet.
          break;
      }
    };
    crossTabChannel.addEventListener('message', onCrossTabMessage);

    return () => {
      crossTabChannel.removeEventListener('message', onCrossTabMessage);
      crossTabChannel.close();
    };
  }, [clearAuthData, crossTabChannel, dispatch]);

  // Cross-tab auth sync when localStorage changes in another tab (Opera-safe; works without BroadcastChannel).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const applyRemoteLogout = () => {
      dispatch({ type: 'logout' });
      queryClient.clear();
      clearAuthToken();
      setAuthToken(undefined);
      setCurrentStorageUserScopeId(undefined);
      destroyControllerRemoteWrapper();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== persistKey) return;

      if (event.newValue === null) {
        applyRemoteLogout();
        return;
      }

      const parsed = safeParseStoredJson(event.newValue, authPersistedStateSchema);
      if (!parsed.ok) {
        applyRemoteLogout();
        return;
      }

      const restored = parsed.data as Omit<State, 'authSettled'>;
      const tokenClearlyExpired =
        typeof restored.token === 'string' &&
        restored.token.length > 0 &&
        isJwtExpired(restored.token, JWT_HYDRATE_EXPIRY_SKEW_MS);
      const remotePayload: Omit<State, 'authSettled'> = tokenClearlyExpired
        ? { ...restored, token: undefined }
        : restored;
      if (typeof remotePayload.token === 'string' && remotePayload.token.length > 0) {
        updateAuthToken(remotePayload.token);
        dispatch({ type: 'hydrate', payload: remotePayload });
      } else if (
        typeof remotePayload.firebaseIdToken === 'string' &&
        remotePayload.firebaseIdToken.length > 0
      ) {
        dispatch({ type: 'hydrate', payload: remotePayload });
      } else {
        applyRemoteLogout();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dispatch]);

  // After startup hydration with persisted auth, try one refresh so expired access JWT can recover
  useEffect(() => {
    if (
      state.state !== 'idle' ||
      (!state.token && !restoreStatus.current.restoredCredentialsFromStorage) ||
      startupRefreshAttemptedRef.current
    ) {
      return;
    }
    startupRefreshAttemptedRef.current = true;
    refreshSessionIfPossible({ reason: 'startup' }).finally(() => {
      // Critical invariant: startup must always move auth out of "pending".
      // Even if refresh was skipped (other lock owner) or failed transiently, the app should stop
      // showing loading/locked placeholders and render the best-known auth state.
      dispatch({ type: 'authSettled' });
    });
  }, [state.state, state.token, refreshSessionIfPossible, dispatch]);

  // sync every state change to local storage except for initial state
  useEffect(() => {
    // sync state changes to storage
    if (state.state === 'initializing') return;

    localStorage.setItem(persistKey, JSON.stringify(state));
  }, [state]);

  const externalState = useMemo<ExternalState>(
    () => ({
      ...state,
      // Access token is the primary auth signal; firebaseIdToken is optional and may lag behind.
      isAuthorized: state.token !== undefined,
      isAuthSettled: state.authSettled === true,
    }),
    [state],
  );

  const authFunctions = useMemo(
    () => ({
      clearAuthData,
      clearAuthDataAndTraining,
    }),
    [clearAuthData, clearAuthDataAndTraining],
  );

  // Immediately sync bearer token into request transport token reference + authStorage.
  if (config.useMockedAuthToken) {
    // set mocked jwt bearer authorization token (ref is read by api request interceptor at send time)
    uiLogger.debug('AuthContext: Using mocked auth token');
    setAuthToken(mockedAuthToken);
    updateAuthToken(mockedAuthToken);
  } else if (state.token) {
    // set real jwt bearer authorization token (ref is read by api request interceptor at send time)
    uiLogger.debug('AuthContext: Using real auth token');
    setAuthToken(state.token);
    updateAuthToken(state.token);
  } else {
    setAuthToken(undefined);
    uiLogger.debug('AuthContext: No auth token available');
  }

  useEffect(() => {
    if (auth === undefined) return;
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // On load/reload Firebase may fire with null before restoring the persisted user (auth state
        // can change several times while the client verifies persistence). See e.g. Firebase team
        // answer https://stackoverflow.com/a/50684130 and firebase-js-sdk#8282 (null in new tab).
        // Do not clear when we restored credentials from localStorage — wait for Firebase to emit the real user.
        if (!restoreStatus.current.firebase || !restoreStatus.current.storage) {
          restoreStatus.current.firebase = true;
          if (restoreStatus.current.restoredCredentialsFromStorage) {
            uiLogger.debug(
              'AuthContext: Ignoring initial Firebase null (credentials restored from storage)',
            );
          }
        } else {
          // Firebase can sporadically emit null (network flake, persistence race).
          // Do NOT clear Turfmaster session based solely on Firebase state — the backend token is authoritative.
          if (!state.token) {
            clearAuthData();
          } else {
            uiLogger.warn(
              'AuthContext: Firebase user became null while access token exists; keeping session',
            );
          }
        }
      } else {
        // use this callback only for firebase auth updates, not for initial login / registration
        // those are handled on the respective screens and this would otherwise lead to duplicated requests
        if (!state.token) return;

        // catch getIdToken error for unset users
        try {
          const firebaseIdToken = await firebaseUser.getIdToken();

          // check if firebaseIdToken is new
          if (state.firebaseIdToken === firebaseIdToken) return;

          // Keep backend session stable: firebase token rotation should NOT trigger another
          // `/auth/refresh` call here. During rapid reloads that creates a refresh storm and can
          // amplify race conditions. The access token remains authoritative; we only persist the
          // latest firebaseIdToken so future flows can use it if needed.
          dispatch({
            type: 'commitCredentials',
            payload: { token: state.token, firebaseIdToken },
          });
        } catch (idTokenError) {
          if (isFirebaseTransientError(idTokenError)) {
            uiLogger.warn(
              { err: idTokenError },
              'transient error reading firebase token; keeping session',
            );
          } else {
            uiLogger.warn(
              { err: idTokenError },
              'error reading refreshed firebase token; keeping session',
            );
            // Keep access token; Firebase token failures should not force-logout the user.
          }
        }
      }
    });
    return unsubscribe;
  }, [auth, clearAuthData, dispatch, refreshSessionIfPossible, state.firebaseIdToken, state.token]);

  // Handle socket reconnection when token is updated
  useEffect(() => {
    const token = state.token ?? null;
    const previousToken = lastSeenAccessTokenRef.current;
    lastSeenAccessTokenRef.current = token;

    // Skip reconnect on initial token hydration/login; reconnect only on real token rotation.
    if (!token || !previousToken || token === previousToken) return;

    const controllerWrapper = getControllerRemoteWrapper();
    if (
      controllerWrapper &&
      controllerWrapper.isConnected() &&
      controllerWrapper.getPurpose?.() === 'live'
    ) {
      uiLogger.info('AuthContext: Access token rotated, triggering controller reconnection');
      controllerWrapper.handleTokenRefresh();
    }
  }, [state.token]);

  return (
    <AuthStateContext.Provider value={externalState}>
      <AuthDispatchContext.Provider value={dispatch}>
        <AuthFunctionsContext.Provider value={authFunctions}>
          {children}
        </AuthFunctionsContext.Provider>
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuthState = (): ExternalState => {
  const context = useContext(AuthStateContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const useAuthDispatch = (): Dispatch<Action> => {
  const context = useContext(AuthDispatchContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const useClearAuthData = () => {
  const context = useContext(AuthFunctionsContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context.clearAuthData;
};

export const useClearAuthDataAndTraining = () => {
  const context = useContext(AuthFunctionsContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context.clearAuthDataAndTraining;
};
